import type { Node, Mark } from "@tiptap/pm/model";
import { MarkdownSerializerState as BaseMarkdownSerializerState } from "prosemirror-markdown";
import { trimInline } from "../util/markdown";
import "./types";

type Inline = { start: number; end?: number; delimiter: string };

type AnyMarkdownSerializerStateCtor = new (
    nodes: Record<string, unknown>,
    marks: Record<string, unknown>,
    options?: Record<string, unknown>,
) => BaseMarkdownSerializerState;

const Base = BaseMarkdownSerializerState as unknown as AnyMarkdownSerializerStateCtor;

/**
 * Override default MarkdownSerializerState to:
 * - handle commonmark delimiters (https://spec.commonmark.org/0.29/#left-flanking-delimiter-run)
 */
export class MarkdownSerializerState extends Base {
    override inlines: Inline[] = [];

    constructor(nodes: Record<string, unknown>, marks: Record<string, unknown>, options?: Record<string, unknown>) {
        super(nodes, marks, options ?? {});
        this.inlines = [];
    }

    override render(node: Node, parent: Node, index: number) {
        super.render(node, parent, index);
        const top = this.inlines[this.inlines.length - 1];
        if (top?.start && top?.end) {
            const { delimiter, start, end } = this.normalizeInline(top as Required<Inline>);
            this.out = trimInline(this.out, delimiter, start, end);
            this.inlines.pop();
        }
    }

    override markString(mark: Mark, open: boolean, parent: Node, index: number): string {
        const info = this.marks[mark.type.name];
        if (info?.expelEnclosingWhitespace) {
            if (open) {
                this.inlines.push({
                    start: this.out.length,
                    delimiter: typeof info.open === "string" ? info.open : "",
                });
            } else {
                const top = this.inlines.pop();
                this.inlines.push({
                    ...top,
                    end: this.out.length,
                } as Inline);
            }
        }
        return super.markString(mark, open, parent, index);
    }

    normalizeInline(inline: Required<Inline>): Required<Inline> {
        let { start } = inline;
        while (this.out.charAt(start).match(/\s/)) {
            start++;
        }
        return {
            ...inline,
            start,
        };
    }

    escapeLiteralText(text: string): string {
        const lineStart = this.atBlockStart || /(?:^|\n)(?:> ?)*[ \t]*$/.test(this.out);
        const escaped = super
            .esc(text, lineStart)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/[=$]/g, "\\$&")
            .replace(/:(?=\/\/)/g, "\\:");
        return lineStart
            ? escaped
                  .replace(/^(\s*\d+)\)(?=\s)/, "$1\\)")
                  .replace(/^[ \t]+/, (indent) => indent.replace(/ /g, "&#32;").replace(/\t/g, "&#9;"))
            : escaped;
    }

    override esc(text: string, startOfLine = false): string {
        const escaped = super.esc(text, startOfLine);
        return this.inlines.some((inline) => inline.delimiter === "==" && inline.end === undefined)
            ? escaped.replaceAll("=", "\\=")
            : escaped;
    }
}
